/* eslint-disable no-unsafe-optional-chaining */
import { yupResolver } from '@hookform/resolvers/yup';
import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useMemo } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { useOnboardingStore } from 'src/store/personalization/onboarding/onboarding.store';
import * as Yup from 'yup';

export default function useOnboardingDetails() {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const {
    isLoadingOnboarding,
    onboarding,
    isLoadingUpdateOnboarding,
    getOnboardingDetails,
    updateOnboarding,
  } = useOnboardingStore();

  useEffect(() => {
    getOnboardingDetails();
  }, []);

  const defaultValues = useMemo(
    () => ({
      startImage: onboarding?.startImage || '',
      startTitle: onboarding?.startTitle || '',
      startDescription: onboarding?.startDescription || '',
      onboardingSteps: onboarding?.onboardingSteps?.map((step) => ({
        stepImage: step.stepImage || '',
        stepTitle: step.stepTitle || '',
        stepDescription: step.stepDescription || '',
      })) || [{ stepImage: '', stepTitle: '', stepDescription: '' }],
    }),
    [onboarding]
  );

  const onboardingSchema = Yup.object().shape({
    startImage: Yup.mixed()
      .required('A imagem inicial é obrigatória')
      .test('not-empty', 'A imagem inicial é obrigatória', (value) => value !== ''),
    startTitle: Yup.string().required('O título inicial é obrigatório'),
    startDescription: Yup.string().required('A descrição inicial é obrigatória'),
    onboardingSteps: Yup.array()
      .of(
        Yup.object().shape({
          stepImage: Yup.mixed().test(
            'not-empty',
            'A imagem do passo é obrigatória',
            (value) => value !== ''
          ),
          stepTitle: Yup.string().required('O título do passo é obrigatório'),
          stepDescription: Yup.string().required('A descrição do passo é obrigatória'),
        })
      )
      .required('Os passos do onboarding são obrigatórios'),
  });

  const methods = useForm({
    resolver: yupResolver(onboardingSchema),
    defaultValues,
  });

  const {
    handleSubmit,
    formState: { isSubmitting },
    setValue,
    control,
    reset,
  } = methods;

  useEffect(() => {
    if (defaultValues) {
      reset(defaultValues);
    }
  }, [onboarding, reset, defaultValues]);

  const onSubmit = handleSubmit(async (data: any) => {
    try {
      await updateOnboarding(data);
      enqueueSnackbar('Onboarding atualizado com sucesso!');
      navigate(0);
    } catch (error) {
      console.error(error);
    }
  });

  const handleDropUrl = useCallback(
    (acceptedFiles: File[]) => {
      if (!acceptedFiles || acceptedFiles.length === 0) return;

      const file = acceptedFiles[0];

      const processedFile = Object.assign(file, {
        preview: URL.createObjectURL(file),
      });

      setValue('startImage', processedFile);
    },
    [setValue]
  );

  const handleRemove = () => {
    setValue('startImage', '');
  };

  const handleRemoveAll = () => {
    setValue('startImage', '');
  };

  const {
    fields: onboardingStagesFields,
    append,
    remove,
  } = useFieldArray({
    control,
    name: 'onboardingSteps',
  });

  const appendOnboardingStage = (p0?: {
    stepImage: string;
    stepTitle: string;
    stepDescription: string;
  }) => {
    append({
      stepImage: '',
      stepTitle: '',
      stepDescription: '',
    });
  };

  const removeOnboardingStage = (index: number) => {
    setValue(`onboardingSteps.${index}.stepImage`, '');
    setValue(`onboardingSteps.${index}.stepTitle`, '');
    setValue(`onboardingSteps.${index}.stepDescription`, '');

    remove(index);
  };

  useEffect(() => {
    if (onboardingStagesFields.length === 0) {
      appendOnboardingStage({
        stepImage: '',
        stepTitle: '',
        stepDescription: '',
      });
    }
  }, [onboardingStagesFields, appendOnboardingStage]);

  const handleDropStepImage = useCallback(
    (index: number, acceptedFiles: File[]) => {
      if (!acceptedFiles || acceptedFiles.length === 0) return;

      const file = acceptedFiles[0];

      const processedFile = Object.assign(file, {
        preview: URL.createObjectURL(file),
      });

      setValue(`onboardingSteps.${index}.stepImage`, processedFile);
    },
    [setValue]
  );

  const handleRemoveStepImage = (index: number) => {
    setValue(`onboardingSteps.${index}.stepImage`, '');
  };

  const handleRemoveAllStepImages = () => {
    onboardingStagesFields.forEach((_, index) => {
      setValue(`onboardingSteps.${index}.stepImage`, '');
    });
  };

  return {
    isLoadingOnboarding,
    onboarding,
    methods,
    isSubmitting,
    onboardingStagesFields,
    isLoadingUpdateOnboarding,
    onSubmit,
    handleDropUrl,
    handleRemove,
    handleRemoveAll,
    appendOnboardingStage,
    removeOnboardingStage,
    handleDropStepImage,
    handleRemoveStepImage,
    handleRemoveAllStepImages,
  };
}
